import * as React from "react";
import PropTypes from "prop-types";
import Button from "@mui/material/Button";
import * as htmlToImage from "html-to-image";
import emailjs from "emailjs-com";

import Api from "../../Api/Api";
import azure from "../../utils/azure";

const instanceCerts = {
  CREWBELT: {
    width: 848.81,
    name: 120,
    title: 180,
    subtitle: 200,
    dateTop: 372,
    dateRight: 195
  },
  MARATHON: {
    width: 806,
    name: 140,
    title: 210,
    subtitle: 220,
    dateTop: 375,
    dateRight: 214
  },
}

const retry = async (fn, attempts = 3, delayMs = 2000) => {
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn();
    } catch (err) {
      if (i === attempts - 1) throw err;
      await new Promise((r) => setTimeout(r, delayMs * Math.pow(2, i)));
    }
  }
};

const RenderCert = ({ data, spanish, template, score }) => {
  const [generating, setGenerating] = React.useState(true);
  const [uploadError, setUploadError] = React.useState(null);
  const [retrying, setRetrying] = React.useState(false);
  const domEl = React.useRef(null);
  const certDone = React.useRef(false);
  const certUrl = React.useRef(null);
  const savedQuizRef = React.useRef(null);
  const [bgImageSrc, setBgImageSrc] = React.useState(null);
  const bgReadyPromiseRef = React.useRef(null);
  const bgReadyResolveRef = React.useRef(null);
  const bgReadyRejectRef = React.useRef(null);
  if (!bgReadyPromiseRef.current) {
    bgReadyPromiseRef.current = new Promise((resolve, reject) => {
      bgReadyResolveRef.current = resolve;
      bgReadyRejectRef.current = reject;
    });
  }

  // Load the background, then open the capture gate ONLY once the image has
  // actually decoded AND painted. On failure, reject the gate so capture/upload
  // never runs without a background (no blank certs).
  const loadBackground = React.useCallback(() => {
    // (re)arm the gate so a previous failure can't poison a retry
    bgReadyPromiseRef.current = new Promise((resolve, reject) => {
      bgReadyResolveRef.current = resolve;
      bgReadyRejectRef.current = reject;
    });

    const url = `/${process.env.REACT_APP_APP_INSTANCE}_BLANK.jpg`;

    (async () => {
      try {
        const blob = await retry(async () => {
          const r = await fetch(url);
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return await r.blob();
        });

        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = e => resolve(e.target.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

        // Confirm the bytes decode to a real, paintable image
        const img = new Image();
        img.src = dataUrl;
        await (img.decode
          ? img.decode()
          : new Promise((res, rej) => { img.onload = res; img.onerror = rej; }));

        setBgImageSrc(dataUrl);

        // Wait for the new background to actually paint before allowing capture
        await new Promise(res =>
          requestAnimationFrame(() => requestAnimationFrame(res))
        );

        bgReadyResolveRef.current?.();
      } catch (err) {
        // Never resolve on failure — capture must NOT proceed without a background
        bgReadyRejectRef.current?.(err);
      }
    })();
  }, []);

  React.useEffect(() => {
    loadBackground();
  }, [loadBackground]);

  const runCertUpload = React.useCallback(async () => {
    // Phase 1: Save quiz record (retryable — backend deduplicates)
    if (!savedQuizRef.current) {
      const { data: savedQuiz } = await retry(() => Api.createCert(data));
      savedQuizRef.current = savedQuiz?.quiz;
    }
    const quiz = savedQuizRef.current;

    await bgReadyPromiseRef.current; // background data URL is in DOM; safe to capture

    // Phase 2: Capture image + upload to Azure (retryable — same filename = overwrite)
    const blob = await htmlToImage.toBlob(document.getElementById("domEl"), {
      quality: 0.95,
      skipFonts: true,
    });
    const url = await retry(() =>
      azure.uploadCertNew(
        blob,
        `${data.userID || data.emp_email}/${data.cert_name}.png`,
        data,
        quiz
      )
    );

    data.certificate_url = url;
    certUrl.current = url;

    // Phase 3: Email (best-effort, no retry — cert is already saved)
    try {
      await emailjs.send(
        process.env.REACT_APP_APP_INSTANCE === "CREWBELT" ? "service_bbme5kk" : "service_27j4zif",
        template,
        data,
        "uLBRMNt_Pnb3kQlS7"
      );
    } catch (emailErr) {
      console.warn("Certificate email failed (cert was saved successfully):", emailErr);
    }

    certDone.current = true;
  }, [data, template]);

  const handleRetry = async () => {
    setRetrying(true);
    setUploadError(null);
    try {
      loadBackground(); // re-arm + re-fetch the background before re-capturing
      await runCertUpload();
    } catch (err) {
      console.error("Certificate retry failed:", err);
      const errMsg = err?.message || String(err);
      setUploadError(
        `CERT-UPLOAD-FAILED | User: ${data.emp_name} | Quiz: ${data.certification_title} | Date: ${data.todays_date} | UserID: ${data.userID || 'MISSING'} | Error: ${errMsg}`
      );
    } finally {
      setRetrying(false);
    }
  };

  React.useEffect(() => {
    const generateAndSendCertificate = async () => {
      try {
        await runCertUpload();
      } catch (err) {
        console.error("Certificate creation failed:", err);
        const errMsg = err?.message || String(err);
        setUploadError(
          `CERT-UPLOAD-FAILED | User: ${data.emp_name} | Quiz: ${data.certification_title} | Date: ${data.todays_date} | UserID: ${data.userID || 'MISSING'} | Error: ${errMsg}`
        );
      } finally {
        setGenerating(false);
      }
    };

    if (!certDone.current) generateAndSendCertificate();
  }, [data, template, runCertUpload]);

  const downloadImage = async () => {
    try {
      const dataUrl = await htmlToImage.toPng(document.getElementById("domEl"), {
        quality: 0.95,
        skipFonts: true,
      });
      const link = document.createElement("a");
      link.download = `${data.cert_name}.png`;
      link.href = dataUrl;
      link.click();
    } catch (error) {
      console.error("Failed to generate image for download:", error);
    }
  };
  return (
    <div>
      {uploadError && (
        <div style={{
          backgroundColor: "#fdecea",
          border: "1px solid #f5c6cb",
          borderRadius: 4,
          padding: "12px 16px",
          margin: "10px 0",
          fontFamily: "monospace",
          fontSize: 13,
          color: "#721c24",
          wordBreak: "break-word",
          userSelect: "all",
        }}>
          <p style={{ margin: "0 0 8px 0", fontWeight: 700, fontFamily: "inherit" }}>
            {spanish
              ? "Error: su certificado no se guardó. Por favor tome una captura de pantalla de este mensaje y envíelo al departamento de formación."
              : "Error: your certificate was not saved. Please screenshot this message and send it to the training department."}
          </p>
          <p style={{ margin: 0, fontFamily: "inherit" }}>{uploadError}</p>
          <Button
            onClick={handleRetry}
            variant="contained"
            disabled={retrying}
            style={{ marginTop: 10 }}
          >
            {retrying
              ? (spanish ? "Reintentando..." : "Retrying...")
              : (spanish ? "Reintentar guardar certificado" : "Retry Saving Certificate")}
          </Button>
        </div>
      )}
      {score >= 70 && (
        <p style={{ textAlign: "center", marginTop: 10, fontWeight: 600 }}>
          {generating
            ? spanish
              ? "Enviando tu certificado por correo electrónico..."
              : "Sending your certificate via email..."
            : uploadError
              ? null
              : spanish
                ? "Hemos enviado una copia de tu certificado a tu correo electrónico y al departamento de formación."
                : "A copy of your certificate has been emailed to you and the training department."}
        </p>
      )}

      <div
        style={{
          overflow: "hidden",
          backgroundColor: "black",
          maxHeight: 200,
          maxWidth: 600,
          position: "relative",
          border: "3px solid #d94d41",
          marginBottom: 10,
        }}
      >
        <Button
          onClick={downloadImage}
          variant="contained"
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 99,
          }}
        >
          {spanish ? "Descargar certificado" : "Download Certificate"}
        </Button>

        <div style={{ opacity: .5 }}>
          <div id="domEl" ref={domEl} style={{ width: instanceCerts[process.env.REACT_APP_APP_INSTANCE].width, height: 600 }}>
            <div
              style={{
                width: instanceCerts[process.env.REACT_APP_APP_INSTANCE].width,
                height: 600,
                position: "relative",
                backgroundImage: `url(${bgImageSrc || `/${process.env.REACT_APP_APP_INSTANCE}_BLANK.jpg`})`,
                backgroundSize: "cover",
              }}
            >
              <h3
                style={{
                  margin: 0,
                  position: "relative",
                  top: instanceCerts[process.env.REACT_APP_APP_INSTANCE].name,
                  fontSize: 30,
                  textAlign: "center",
                }}
              >
                {data.emp_name}
              </h3>
              <h3
                style={{
                  margin: 0,
                  position: "relative",
                  top: instanceCerts[process.env.REACT_APP_APP_INSTANCE].title,
                  fontSize: 25,
                  textAlign: "center",
                }}
              >
                {data.certification_title || ""}
              </h3>
              {data.certificate_subtitle && <p
                style={{
                  margin: 0,
                  position: "relative",
                  top: instanceCerts[process.env.REACT_APP_APP_INSTANCE].subtitle,
                  fontSize: 20,
                  textAlign: "center",
                }}
              >
                {data.certificate_subtitle}
              </p>}
              <p
                style={{
                  margin: 0,
                  position: "absolute",
                  top: instanceCerts[process.env.REACT_APP_APP_INSTANCE].dateTop,
                  fontSize: 15,
                  textAlign: "center",
                  right: instanceCerts[process.env.REACT_APP_APP_INSTANCE].dateRight,
                }}
              >
                {data.todays_date}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

RenderCert.propTypes = {
  data: PropTypes.object.isRequired,
  spanish: PropTypes.bool.isRequired,
  template: PropTypes.string.isRequired,
  score: PropTypes.number.isRequired,
};

export default RenderCert;
